import { AnimationBuilder, AnimationMetadata } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Renderer2,
  Type,
  ViewChild,
  ViewContainerRef,
  ViewRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { ActivatedModal, ModalAnimationType, ModalOptions } from '../data/modal.data';
import { ModalService } from '../services/modal.service';
import {
  fadeInAnimation,
  fadeOutAnimation,
  slideLeftInAnimation,
  slideLeftOutAnimation,
  slideRightInAnimation,
  slideRightOutAnimation,
} from './modal.transitions';

@Component({
  selector: 'app-modal',
  template: `
    <div class="overlay"></div>
    <div class="container" #container>
      <ng-template #templateRef></ng-template>
    </div>
  `,
  standalone: true,
  styleUrl: 'modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent {
  @ViewChild('templateRef', { read: ViewContainerRef, static: false })
  viewContainerRef!: ViewContainerRef;
  @ViewChild('container', { static: true })
  containerRef!: ElementRef<HTMLElement>;

  get isActive(): boolean {
    return !!this.activeModal;
  }
  private hostClass = '';
  private activeModal?: ActivatedModal;
  private isMousedownOutside?: boolean;
  private modalViews: ActivatedModal[] = [];

  constructor(
    modalService: ModalService,
    private elementRef: ElementRef<HTMLElement>,
    private animationBuilder: AnimationBuilder,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    modalService.init(this);
    fromEvent<KeyboardEvent>(this.document.body, 'keyup')
      .pipe(takeUntilDestroyed())
      .subscribe(e => this.onWindowKeyup(e));
  }

  private onWindowKeyup(e: KeyboardEvent): void {
    if (this.activeModal?.options?.obsessive) {
      return;
    }
    if (this.activeModal && (e.keyCode ? e.keyCode : e.which) === 27) {
      this.close();
    }
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(e: MouseEvent): void {
    if (this.activeModal?.options?.obsessive) {
      return;
    }
    const parent = this.renderer.parentNode(e.target) as HTMLElement;
    const emptySpaceClick = parent === this.containerRef.nativeElement || parent === this.elementRef.nativeElement;
    if (
      emptySpaceClick &&
      e.button === 0 &&
      e.target &&
      e.target !== this.activeModal?.component.location.nativeElement
    ) {
      this.isMousedownOutside = true;
    }
  }

  @HostListener('mouseup')
  onClick(): void {
    if (this.activeModal?.options?.obsessive) {
      return;
    }
    if (this.isMousedownOutside) {
      this.close();
    }
    this.isMousedownOutside = false;
  }

  open<T>(type: Type<T>, options?: ModalOptions<T>, animation?: ModalAnimationType, modalRef?: ActivatedModal<T>) {
    animation = animation || (this.activeModal ? 'slide-left' : 'fade');
    if (this.activeModal) {
      const modal = this.activeModal;
      this.animate(this.getAnimation(animation, 'leave'), modal.component.location.nativeElement, () => {
        modalRef ? this.viewContainerRef.remove(0) : this.viewContainerRef.detach(0);
        (modal.component.location.nativeElement as HTMLElement)?.remove();
      });
    }
    const component = modalRef?.component || this.viewContainerRef.createComponent(type);
    if (component) {
      if (!modalRef?.viewRef) {
        this.hostClass
          .split(' ')
          .filter(c => !!c)
          .forEach(c => {
            this.renderer.removeClass(this.elementRef.nativeElement, c);
          });
        `${options?.hostClass || 'default'} modal-element`.split(' ').forEach(c => {
          this.renderer.addClass(component.location.nativeElement, c);
        });
        this.hostClass = options?.hostClass || '';
        this.hostClass
          .split(' ')
          .filter(c => !!c)
          .forEach(c => {
            this.renderer.addClass(this.elementRef.nativeElement, c);
          });
      }
      if (options?.onOpen) {
        options.onOpen(component.instance);
      }
      this.animate(this.getAnimation(animation, 'enter'), component.location.nativeElement);
      if (!modalRef) {
        const modal = {
          type,
          viewRef: this.viewContainerRef.get(this.modalViews.length) as ViewRef,
          component,
          options: options as ModalOptions<unknown>,
        };
        this.activate(modal);
        this.modalViews.push(modal);
      } else {
        this.viewContainerRef.insert(modalRef.viewRef, 1);
        this.activate(modalRef as ActivatedModal<unknown>);
      }
    }
    return component;
  }

  getAnimation(animation: ModalAnimationType, type: 'enter' | 'leave' = 'enter') {
    if (animation === 'fade') {
      return type === 'enter' ? fadeInAnimation : fadeOutAnimation;
    } else if (animation === 'slide-left') {
      return type === 'enter' ? slideLeftInAnimation : slideLeftOutAnimation;
    } else {
      return type === 'enter' ? slideRightInAnimation : slideRightOutAnimation;
    }
  }

  private animate(animation: AnimationMetadata[], element: HTMLElement, done = () => {}) {
    const factory = this.animationBuilder.build(animation);
    const player = factory.create(element);
    player.play();
    player.onDone(() => {
      player.destroy();
      done();
    });
  }

  private activate(modal: ActivatedModal) {
    if (!this.activeModal) {
      this.renderer.addClass(this.document.body, 'modal-active');
      if (modal.options?.noOverlay) {
        this.renderer.addClass(this.document.body, 'no-overlay');
      } else {
        this.renderer.addClass(this.document.body, 'blur-on');
      }
    }
    this.activeModal = modal;
  }

  back() {
    this.modalViews.pop();
    if (!this.modalViews.length) {
      this.close();
      this.viewContainerRef.remove(0);
    } else {
      const modal = this.modalViews[this.modalViews.length - 1];
      if (this.activeModal?.options?.onClose) {
        this.activeModal.options.onClose(this.activeModal.component.instance);
      }
      this.open(modal.type, modal.options, 'slide-right', modal);
    }
  }

  close() {
    if (!this.activeModal) {
      return;
    }
    this.renderer.addClass(this.document.body, 'modal-deactivating');
    this.renderer.removeClass(this.document.body, 'blur-on');
    if (this.activeModal.options?.onClose) {
      this.activeModal.options.onClose(this.activeModal.component.instance);
    }
    const modal = this.activeModal;
    this.animate(fadeOutAnimation, this.activeModal.component.location.nativeElement, () => {
      this.viewContainerRef.clear();
      (modal?.component.location.nativeElement as HTMLElement)?.remove();
      this.modalViews = [];
      this.renderer.removeClass(this.document.body, 'modal-active');
      this.renderer.removeClass(this.document.body, 'modal-deactivating');
    });
    this.activeModal = undefined;
  }
}
